/** Editor для Розукомплектації номенклатури (підфіча картки #3, alembic 0262). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import {
  productDisassemblies,
  type ProductDisassemblyComponentIn,
} from '@/api/productDisassemblies';
import { nomenclatureSpecifications } from '@/api/nomenclatureSpecifications';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import SearchSelect from '@/components/SearchSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.assembly.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  disassembled_nomenclature_id: string;
  disassembled_quantity: string;
  specification_id: string;
  disassembled_account_code: string;
  comment: string;
  project_id: string | null;
  components: ProductDisassemblyComponentIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  disassembled_nomenclature_id: '',
  disassembled_quantity: '1',
  specification_id: '',
  disassembled_account_code: '',
  comment: '',
  project_id: null,
  components: [],
};

const newRow = (n: number): ProductDisassemblyComponentIn => ({
  line_number: n, nomenclature_id: '', quantity: '0', cost_percentage: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProductDisassemblyEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<'lines' | 'accounts'>('lines');
  // ч.673: набір обирається попапом (з залишків, несе склад); компоненти —
  // попапом мультивибору (склад оприбуткування НЕ береться з попапу).
  const [kitPickerOpen, setKitPickerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => import('@/api/documents').then((m) => m.accounts.list({ only_postable: true, limit: 1000 })),
  });
  const nomAllQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => import('@/api/catalogs').then((m) => m.nomenclature.listAll()),
  });
  const docQuery = useQuery({
    queryKey: ['product-disassembly', id],
    queryFn: () => productDisassemblies.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.product_disassemblies', form.organization_id, isNew);

  // Активні ASSEMBLY-специфікації обраного набору — для автопідстановки складу+долей.
  const specsQ = useQuery({
    queryKey: ['assembly-specs', form.disassembled_nomenclature_id],
    queryFn: () =>
      nomenclatureSpecifications.list({
        product_nomenclature_id: form.disassembled_nomenclature_id,
        purpose: 'ASSEMBLY',
        is_active: true,
        limit: 100,
      }),
    enabled: !!form.disassembled_nomenclature_id,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        disassembled_nomenclature_id: d.disassembled_nomenclature_id,
        disassembled_quantity: d.disassembled_quantity,
        specification_id: d.specification_id ?? '',
        disassembled_account_code: d.disassembled_account_code ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        components: d.components.map((c) => ({
          line_number: c.line_number,
          nomenclature_id: c.nomenclature_id,
          quantity: c.quantity,
          cost_percentage: c.cost_percentage,
          warehouse_id: c.warehouse_id ?? null,
          account_code: c.account_code ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const totalPct = useMemo(
    () => form.components.reduce((acc, c) => acc + (Number(c.cost_percentage) || 0), 0),
    [form.components],
  );
  const pctOk = form.components.length > 0 && Math.abs(totalPct - 100) < 0.005;

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    warehouse_id: form.warehouse_id,
    disassembled_nomenclature_id: form.disassembled_nomenclature_id,
    disassembled_quantity: form.disassembled_quantity,
    specification_id: form.specification_id || null,
    disassembled_account_code: form.disassembled_account_code || null,
    comment: form.comment || null,
        project_id: form.project_id,
    components: form.components,
  });

  const create = useMutation({
    mutationFn: () => productDisassemblies.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
      navigate(`/product-disassemblies/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => productDisassemblies.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-disassembly', id] });
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productDisassemblies.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await productDisassemblies.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/product-disassemblies/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
      navigate(`/product-disassemblies/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productDisassemblies.update(id!, buildPayload());
      await productDisassemblies.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-disassembly', id] });
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => productDisassemblies.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-disassembly', id] });
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => productDisassemblies.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-disassemblies'] });
      navigate('/product-disassemblies');
    },
    onError: (e) => setError(extractError(e)),
  });

  // ч.673: компоненти додаються попапом (мультивибір). Склад оприбуткування
  // з попапу НЕ береться — за замовчуванням склад набору (рядок правиться руками).
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      components: [...f.components, ...rows.map((row, k) => ({
        ...newRow(f.components.length + 1 + k),
        nomenclature_id: row.nomenclature_id,
        quantity: '1',
      }))],
    }));
  }
  function removeComponent(i: number) {
    setForm((f) => ({
      ...f,
      components: f.components.filter((_, idx) => idx !== i).map((c, idx) => ({ ...c, line_number: idx + 1 })),
    }));
  }
  function setComponentField(i: number, patch: Partial<ProductDisassemblyComponentIn>) {
    setForm((f) => ({ ...f, components: f.components.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  }

  async function fillFromSpecification() {
    if (!form.specification_id) return;
    setError(null);
    try {
      const spec = await nomenclatureSpecifications.get(form.specification_id);
      setForm((f) => ({
        ...f,
        components: spec.items
          .slice()
          .sort((a, b) => a.line_number - b.line_number)
          .map((it, idx) => ({
            line_number: idx + 1,
            nomenclature_id: it.raw_material_nomenclature_id,
            quantity: it.quantity,
            cost_percentage: it.cost_percentage ?? '0',
          })),
      }));
    } catch (e) {
      setError(extractError(e));
    }
  }

  // ч.672: превʼю авто-рахунків (правило/картка → 26) для вкладки
  // «Рахунки обліку» — замість неінформативного «(деф. авто)».
  const previewNomIds = useMemo(() => {
    const ids = new Set<string>();
    if (form.disassembled_nomenclature_id) ids.add(form.disassembled_nomenclature_id);
    form.components.forEach((c) => { if (c.nomenclature_id) ids.add(c.nomenclature_id); });
    return [...ids].sort();
  }, [form.disassembled_nomenclature_id, form.components]);
  const accPreviewQ = useQuery({
    queryKey: ['kit-acc-preview', form.organization_id, form.warehouse_id, previewNomIds],
    queryFn: async () => {
      const out = new Map<string, string>();
      await Promise.all(previewNomIds.map(async (nid) => {
        try {
          const p = await refsApi.accountRules.preview({
            organization_id: form.organization_id,
            nomenclature_id: nid,
            warehouse_id: form.warehouse_id || undefined,
            column: 'account_id',
          });
          out.set(nid, p.account_code ?? '26');
        } catch { out.set(nid, '26'); }
      }));
      return out;
    },
    enabled: viewTab === 'accounts' && !!form.organization_id && previewNomIds.length > 0,
  });
  const autoAcc = (nid: string) => accPreviewQ.data?.get(nid) ?? '…';

  const acctItems = (accountsQ.data ?? []).map((a) => ({
    id: a.code, label: `${a.name} — ${a.code}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // ч.911 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'kit', title: 'Набір', required: 3,
      filled: (form.disassembled_nomenclature_id ? 1 : 0) + (form.warehouse_id ? 1 : 0)
        + (Number(form.disassembled_quantity) > 0 ? 1 : 0) },
    { id: 'components', title: 'Компоненти', required: 1, filled: form.components.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова розукомплектація' : `Розукомплектація: ${form.document_number}`}
        </h2>
        <RequirePermission code={PERMISSION}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            entriesDoc={{ type: 'doc.product_disassemblies', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
            onBackToList={() => navigate('/product-disassemblies')}
            onSave={() => (isNew ? create.mutate() : update.mutate())}
            saveDisabled={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
            postDisabled={!pctOk}
            postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
            postTitle={pctOk ? 'Зберігає та проводить документ' : 'Сума долей собівартості має дорівнювати 100%'}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/product-disassemblies/${id}/print`)}
          />
          </div>
        </RequirePermission>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.product_disassemblies', id: isNew ? '' : id! }} />
      }>
      {/* ч.673 (розкладка власника): [Юрособа|Номер|Дата] →
          [обрати комплект|Набір(read-only)|Склад набору(read-only)|К-сть].
          ч.911 — блоками; коментар — в останньому блоці. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Набір">
      <div className="doc-grid">
        {!isPosted && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>&nbsp;</label>
            <button type="button" onClick={() => setKitPickerOpen(true)}
              disabled={!form.organization_id}
              title={!form.organization_id ? 'Спершу оберіть юрособу'
                : 'Обрати набір з залишків — заповнить номенклатуру і склад'}>
              обрати комплект
            </button>
          </div>
        )}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Набір (номенклатура) *</label>
          <input value={(() => {
              const n = nomAllQ.data?.items.find((x) => x.id === form.disassembled_nomenclature_id);
              return n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '';
            })()}
            readOnly disabled placeholder="— оберіть кнопкою зліва —" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Склад набору (списання) *</label>
          <input value={(() => {
              const w = warehousesQ.data?.find((x) => x.id === form.warehouse_id);
              return w ? `${w.name} — ${w.code}` : '';
            })()}
            readOnly disabled placeholder="— з попапу —" />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Кількість набору *</label>
          <input type="number" step="0.001" value={form.disassembled_quantity}
                 onChange={(e) => setForm({ ...form, disassembled_quantity: e.target.value })} disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      {/* ч.911: кнопки ТЧ — у шапці блоку (заповнення — btn-doc, додавання — primary). */}
      <DocBlock index={3} title="Компоненти"
        actions={!isPosted && viewTab === 'lines' ? (<>
          <button
            type="button"
            className="btn-doc"
            onClick={fillFromSpecification}
            disabled={!form.disassembled_nomenclature_id || !form.specification_id}
            title={
              !form.disassembled_nomenclature_id
                ? 'Спершу оберіть набір'
                : !form.specification_id
                  ? 'Оберіть специфікацію складу'
                  : 'Замінити компоненти складом зі специфікації'
            }
          >
            заповнити зі специфікації
          </button>
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть юрособу'
              : 'Підбір компонентів (мультивибір; склад оприбуткування — за замовчуванням склад набору)'}>
            + додати компонент
          </button>
        </>) : undefined}>
      {/* ч.667: вкладки — канон реалізації. ч.673: специфікація — одразу
          за назвою вкладки (як у комплектації). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' ? 'active' : ''}
            onClick={() => setViewTab('lines')}>Компоненти ({form.components.length})</button>
        </div>
        {!isPosted && viewTab === 'lines' && (
          <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <span
              style={{ width: 260 }}
              title={!form.disassembled_nomenclature_id ? 'Спершу оберіть набір' : undefined}
            >
              <SearchSelect
                items={(specsQ.data?.items ?? []).map((s) => ({ id: s.id, label: s.name }))}
                value={form.specification_id}
                onChange={(v) => setForm({ ...form, specification_id: v })}
                disabled={!form.disassembled_nomenclature_id}
                placeholder="— специфікація складу —"
              />
            </span>
          </span>
        )}
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Рахунок набору</label>
              {acctSelect(form.disassembled_account_code,
                (v) => setForm({ ...form, disassembled_account_code: v ?? '' }), `авто: ${autoAcc(form.disassembled_nomenclature_id)}`)}
            </div>
          </div>
          <TchTable style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Компонент</th>
                <th style={{ width: 220 }}>Рах. обліку</th>
              </tr>
            </thead>
            <tbody>
              {form.components.filter((c) => c.nomenclature_id).map((c) => {
                const i = form.components.indexOf(c);
                const n = nomAllQ.data?.items.find((x) => x.id === c.nomenclature_id);
                return (
                  <tr key={i}>
                    <td>{n ? `${n.name}${n.code ? ' — ' + n.code : ''}` : '—'}</td>
                    <td>{acctSelect(c.account_code, (v) => setComponentField(i, { account_code: v }), `авто: ${autoAcc(c.nomenclature_id)}`)}</td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожньо — застосується рахунок у дужках (правило/картка номенклатури, інакше 26). Оберіть свій, щоб перебити.
          </p>
        </div>
      )}

      {viewTab === 'lines' && (<>
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Компонент</th>
            <th style={{ width: 170 }}>Склад оприбуткування</th>
            <th style={{ width: 100 }}>К-сть</th>
            <th style={{ width: 130 }}>Доля собів., %</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.components.map((c, i) => (
            <tr key={i}>
              <td>{c.line_number}</td>
              <td>
                <NomenclatureSelect value={c.nomenclature_id}
                  onChange={(v) => setComponentField(i, { nomenclature_id: v })}
                  disabled={isPosted} kind="goods" />
              </td>
                            <td>
                <select value={c.warehouse_id ?? ''}
                        onChange={(e) => setComponentField(i, { warehouse_id: e.target.value || null })}
                        disabled={isPosted} style={{ width: '100%' }}>
                  <option value="">— склад набору —</option>
                  {warehousesQ.data?.map((w) => (
                    <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                  ))}
                </select>
              </td>
<td>
                <input type="number" step="0.001" value={c.quantity}
                       onChange={(e) => setComponentField(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" value={c.cost_percentage}
                       onChange={(e) => setComponentField(i, { cost_percentage: e.target.value })}
                       disabled={isPosted} style={{ textAlign: 'right' }} />
              </td>
              <td>
                {!isPosted && <button onClick={() => removeComponent(i)} title="Видалити">×</button>}
              </td>
            </tr>
          ))}
        </tbody>
        {form.components.length > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                Разом доля:
              </td>
              <td
                style={{ textAlign: 'right', fontWeight: 600, color: pctOk ? '#15803d' : '#dc2626' }}
                title={pctOk ? undefined : 'Сума долей собівартості має дорівнювати 100%'}
              >
                {totalPct.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%
              </td>
              <td />
            </tr>
          </tfoot>
        )}
      </TchTable>
      </>)}
      {!pctOk && form.components.length > 0 && (
        <div style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.85rem' }}>
          Сума долей собівартості має дорівнювати 100% — інакше провести документ
          не можна.
        </div>
      )}

      {/* ч.673: попап вибору НАБОРУ — рядок з залишків несе номенклатуру + склад. */}
      {kitPickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || null}
          catalogMode
          expandByWarehouse
          defaultOnlyWithStock
          onlyKind="goods"
          onPick={(row) => {
            setForm((f) => ({
              ...f,
              disassembled_nomenclature_id: row.nomenclature_id,
              warehouse_id: row.warehouse_id || '',
              specification_id: '',
            }));
            setKitPickerOpen(false);
          }}
          onClose={() => setKitPickerOpen(false)}
        />
      )}

      {/* ч.673: попап компонентів — мультивибір; склад оприбуткування з
          попапу НЕ підставляємо (дефолт — склад набору). */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.product_disassemblies" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
